// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck
/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * @format
 * @flow
 */

import * as React from 'react';
import {
  Keyboard,
  LayoutAnimation,
  Platform,
  StyleSheet,
  View,
  Dimensions,
  EmitterSubscription,
} from 'react-native';
import {getBottomSpace} from 'react-native-iphone-x-helper';

type Props = JSX.LibraryManagedAttributes<
  typeof View,
  React.ComponentProps<typeof View>
> & {
  insideTab?: boolean;
  children: React.ReactNode;
  behavior?: string;
  additionalKeyboardHeight?: number;
};

type State = {
  keyboardHeight: number;
};

const TAB_BAR_HEIGHT = 49;

/**
 * View that moves out of the way when the keyboard appears by automatically
 * adjusting its height, position, or bottom padding.
 */
class KeyboardAvoidingView extends React.Component<Props, State> {
  _subscriptions: Array<EmitterSubscription> = [];

  static defaultProps = {
    insideTab: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      keyboardHeight: 0,
    };
  }

  _onKeyboardChange = (event: any | null | undefined) => {
    if (event == null) {
      this.setState({
        keyboardHeight: 0,
      });
      return;
    }

    const window = Dimensions.get('window');
    const {duration, easing, endCoordinates} = event;
    let keyboardHeight =
      window.height - endCoordinates.screenY - getBottomSpace();

    if (this.props.insideTab) {
      keyboardHeight -= TAB_BAR_HEIGHT;
    }

    const height = Math.max(keyboardHeight, 0);

    if (this.state.keyboardHeight === height) {
      return;
    }

    if (duration && easing) {
      LayoutAnimation.configureNext({
        duration: duration > 10 ? duration : 10,
        update: {
          duration: duration > 10 ? duration : 10,
          type: LayoutAnimation.Types[easing] || 'keyboard',
        },
      });
    }

    this.setState({
      keyboardHeight: height,
    });
  };

  _onKeyboardDidShow = (event: any) => {
    if (Platform.OS !== 'android' || Platform.Version <= 34) return;

    const keyboardHeight = event.endCoordinates
      ? event.endCoordinates.height
      : event.end?.height || 0;

    let finalHeight = keyboardHeight;

    if (this.props.insideTab) {
      finalHeight -= TAB_BAR_HEIGHT;
    }

    const height = Math.max(finalHeight, 0);

    if (this.state.keyboardHeight !== height) {
      this.setState({
        keyboardHeight: height,
      });
    }
  };

  _onKeyboardDidHide = () => {
    if (Platform.OS !== 'android' || Platform.Version <= 34) return;

    this.setState({
      keyboardHeight: 0,
    });
  };

  componentDidMount(): void {
    if (Platform.OS === 'ios') {
      this._subscriptions = [
        Keyboard.addListener('keyboardWillChangeFrame', this._onKeyboardChange),
      ];
    } else if (Platform.OS === 'android') {
      this._subscriptions = [
        Keyboard.addListener('keyboardDidShow', this._onKeyboardDidShow),
        Keyboard.addListener('keyboardDidHide', this._onKeyboardDidHide),
      ];

      if (Platform.OS === 'android' && LayoutAnimation.configureNext) {
        LayoutAnimation.setLayoutAnimationEnabledExperimental?.(true);
      }
    }
  }

  componentWillUnmount(): void {
    this._subscriptions.forEach((subscription) => {
      subscription.remove();
    });
  }

  render(): React.ReactNode {
    return (
      <View style={styles.container}>
        <View style={styles.container} {...this.props}>
          {this.props.children}
        </View>
        <View
          style={{
            height:
              this.state.keyboardHeight +
              (this.props.additionalKeyboardHeight || 0),
          }}
        />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default KeyboardAvoidingView;
